import React, { useState, useEffect, useRef } from "react";
import { Upload, Building2, Loader2 } from "lucide-react";
import { useAuth } from "src/contexts/AuthContext";
import { useToast } from "src/components/ui/toast";
import { fetchOrganization, updateOrganization } from "src/apis/organizations";
import { Button } from "src/components/ui/button";

export default function OrganizationGeneralPage() {
  const { organization, setOrganization } = useAuth();
  const { toast } = useToast();

  const [name, setName] = useState(organization?.name || "");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(organization?.logo_url || null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch the latest organization details on mount
  useEffect(() => {
    async function loadOrg() {
      setIsFetching(true);
      try {
        const { data } = await fetchOrganization();
        if (data?.organization) {
          setOrganization(data.organization);
          setName(data.organization.name || "");
          setLogoPreview(data.organization.logo_url || null);
        }
      } catch (error) {
        console.error("Failed to fetch organization details:", error);
      } finally {
        setIsFetching(false);
      }
    }
    loadOrg();
  }, [setOrganization]);

  // Sync state if organization details change externally
  useEffect(() => {
    if (organization) {
      setName((prev) => (prev === "" ? organization.name || "" : prev));
      setLogoPreview(organization.logo_url || null);
    }
  }, [organization]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Organization name cannot be blank.",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await updateOrganization({
        name: name.trim(),
        logo: logoFile,
      });

      if (data?.organization) {
        setOrganization(data.organization);
        setName(data.organization.name || "");
        setLogoPreview(data.organization.logo_url || null);
        setLogoFile(null); // Clear selected file state
        toast({
          title: "Settings saved",
          description: "Organization details have been updated successfully.",
        });
      }
    } catch (error) {
      const errMsg =
        error.response?.data?.errors?.join(", ") ||
        "Failed to update organization details.";
      toast({
        variant: "destructive",
        title: "Error",
        description: errMsg,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-slate-900">
          Organization Settings
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Manage your organization name, profile branding, and logo.
        </p>
      </div>

      {isFetching ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Field */}
          <div className="space-y-2">
            <label
              htmlFor="org_name"
              className="text-[13px] font-semibold text-slate-700"
            >
              Organization Name
            </label>
            <input
              id="org_name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter organization name"
              className="ml-2 h-10 w-full max-w-md rounded-lg border border-slate-200 bg-white px-3 text-[14px] text-slate-800 placeholder:text-slate-400 transition-colors focus:border-[#4b5bed] focus:outline-none focus:ring-2 focus:ring-[#4b5bed]/20 disabled:opacity-50"
              disabled={isLoading}
            />
          </div>

          {/* Logo Field */}
          <div className="space-y-3">
            <label className="text-[13px] font-semibold text-slate-700">
              Logo / Icon
            </label>
            <div className="flex items-center gap-4">
              {/* Logo Preview - Circular, fixed dimensions */}
              <div
                className="relative flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-slate-50"
                style={{
                  width: "64px",
                  height: "64px",
                }}
              >
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Org Logo"
                    className="h-full w-full object-cover rounded-full"
                  />
                ) : (
                  <Building2 className="h-6 w-6 text-slate-400" />
                )}
              </div>

              {/* Upload Action */}
              <div className="space-y-1">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleUploadClick}
                  disabled={isLoading}
                  className="gap-1.5"
                >
                  <Upload className="h-4 w-4" />
                  Upload logo
                </Button>
                <p className="text-[11px] text-slate-400">
                  PNG, JPG, or GIF up to 5MB. Square image suggested.
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-start">
            <Button
              type="submit"
              disabled={isLoading || !name.trim()}
              size="sm"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Saving Changes
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
